import type { MandateDeviceRow } from "@/forwards2/forwardsTypes";
import { isTechnologyKey } from "@/types/technology";

/** The name's first word for a mandate of several companies, where no one company's name fits. */
export const MULTI_COMPANY_NAME = "Multi-company";

/**
 * The name a new mandate is offered before its creator types one: `{company} {country} {technology}
 * {unsupported} {ecolabel}`, e.g. "Acme OÜ EE wind unsupported ecolabel".
 *
 * The first word is the company's name for a mandate of one, `MULTI_COMPANY_NAME` otherwise. Each
 * other word appears only when every one of the mandate's devices matches it, so the name never claims
 * more than the whole mandate delivers. English on purpose: every member and Soldera read the name,
 * whatever their own language (user decision, 2026-10-09).
 *
 * @param memberNames - the mandate's companies, by name
 * @param devices - the mandate's own devices: every member device, or only the allowlisted ones
 */
export function defaultMandateName(
  memberNames: readonly string[],
  devices: readonly MandateDeviceRow[]
): string {
  const company =
    memberNames.length === 1 ? memberNames[0] : MULTI_COMPANY_NAME;
  if (devices.length === 0) return company;

  const words = [
    company,
    shared(devices, (device) => device.country),
    shared(devices, technologyWord),
    devices.every((device) => device.support === false) ? "unsupported" : null,
    devices.every((device) => device.ecolabels.length > 0) ? "ecolabel" : null,
  ];
  return words.filter((word) => word !== null).join(" ");
}

/** The value every device has, or null when they differ or one has none. */
function shared(
  devices: readonly MandateDeviceRow[],
  valueOf: (device: MandateDeviceRow) => string | null
): string | null {
  const values = new Set(devices.map(valueOf));
  if (values.size !== 1) return null;
  return [...values][0];
}

/** The technology as its English word (`wind`), not the registry's code. */
function technologyWord(device: MandateDeviceRow): string | null {
  const key = device.energy_type;
  return key !== null && isTechnologyKey(key) ? key : null;
}
